import type { LocationQuery } from 'vue-router';
import type { MetaOptions } from '@/composables/useMeta';
import type { GalleryDetail } from '@/modules/gallery/domain/entities/GalleryDetail';
import type { PublicProfile } from '@/types';
import type { PublicCategoryEntity } from '@/modules/category';
import { selectSocialImage } from '@/modules/seo/images';
import { getSiteSettings } from '@/modules/seo/siteSettings';
import { linkPageUrl } from '@/modules/link-page/domain/entities/LinkPage';
import type { PublicLinkPageResult } from '@/modules/link-page/domain/entities/LinkPage';

export function buildLinkPageMeta(siteOrigin: string, address: string, result: PublicLinkPageResult): MetaOptions {
  if (result.status === 'unavailable') return { robots: 'noindex,follow' };

  const title = result.page.name ?? address;
  const canonical = `${linkPageUrl(siteOrigin, address)}/`;
  return { title, canonical, robots: 'index,follow', og: { title, url: canonical, type: 'website' } };
}

function normalizeLocale(locale: string): string {
  if (!locale) return 'en_US';

  const normalized = locale.replace('-', '_');
  if (normalized.includes('_')) {
    return normalized;
  }

  switch (normalized.toLowerCase()) {
    case 'es':
      return 'es_ES';
    case 'pt':
      return 'pt_PT';
    default:
      return 'en_US';
  }
}

function ensureLeadingSlash(path: string): string {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function absoluteUrl(origin: string, path: string): string {
  return `${origin}${ensureLeadingSlash(path)}`;
}

function baseMeta(origin: string, locale: string, path: string): Pick<MetaOptions, 'canonical' | 'og' | 'twitter'> {
  const canonical = absoluteUrl(origin, path);
  const ogLocale = normalizeLocale(locale);
  const settings = getSiteSettings();

  return {
    canonical,
    og: {
      type: 'website',
      url: canonical,
      siteName: settings.site_title,
      locale: ogLocale,
    },
    twitter: {
      card: 'summary_large_image',
    },
  };
}

export function getPublicOrigin(): string {
  if (import.meta.env.SSR) {
    const publicOrigin = process.env.SSR_PUBLIC_ORIGIN;
    if (publicOrigin) {
      return publicOrigin;
    }

    if (process.env.NODE_ENV === 'production') {
      throw new Error('SSR_PUBLIC_ORIGIN is required in production SSR runtime.');
    }

    return 'http://localhost:3011';
  }

  return window.location.origin;
}

export function buildHomeMeta(origin: string, locale: string): MetaOptions {
  const settings = getSiteSettings();
  const title = settings.seo_default_title;
  const description = settings.seo_default_description;
  const image = selectSocialImage({ kind: 'home', origin });
  const meta = baseMeta(origin, locale, '/');

  return {
    ...meta,
    title,
    description,
    og: {
      ...meta.og,
      title,
      description,
      image,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
      image,
    },
  };
}

export function buildGalleriesMeta(
  origin: string,
  locale: string,
  options?: { hasFilters?: boolean }
): MetaOptions {
  const settings = getSiteSettings();
  const title = `Galerías públicas | ${settings.site_title}`;
  const description = `Descubre galerías públicas de fotografía artística en ${settings.site_title}.`;
  const image = selectSocialImage({ kind: 'home', origin });
  const meta = baseMeta(origin, locale, '/galleries');

  return {
    ...meta,
    title,
    description,
    robots: options?.hasFilters ? 'noindex,follow' : 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
      image,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
      image,
    },
  };
}

export function buildCategoryMeta(
  origin: string,
  locale: string,
  category: PublicCategoryEntity | null,
  slug: string,
  options?: { notFound?: boolean }
): MetaOptions {
  const settings = getSiteSettings();
  const image = selectSocialImage({ kind: 'home', origin });
  const meta = baseMeta(origin, locale, `/category/${slug}`);

  if (options?.notFound) {
    const title = `Category not found | ${settings.site_title}`;
    const description = `The category you are looking for does not exist or is no longer available on ${settings.site_title}.`;

    return {
      ...meta,
      title,
      description,
      robots: 'noindex,follow',
      og: { ...meta.og, title, description, image },
      twitter: { ...meta.twitter, title, description, image },
    };
  }

  if (!category) {
    const title = `Category | ${settings.site_title}`;
    const description = `Explore public galleries by category on ${settings.site_title}.`;

    return {
      ...meta,
      title,
      description,
      robots: 'index,follow',
      og: { ...meta.og, title, description, image },
      twitter: { ...meta.twitter, title, description, image },
    };
  }

  const title = `${category.name} galleries | ${settings.site_title}`;
  const description = category.description
    ?? `Explore ${category.name} galleries and related photography collections on ${settings.site_title}.`;

  return {
    ...meta,
    title,
    description,
    robots: 'index,follow',
    og: { ...meta.og, title, description, image },
    twitter: { ...meta.twitter, title, description, image },
  };
}

export function buildGalleryDetailMeta(
  origin: string,
  locale: string,
  gallery: GalleryDetail | null,
  path: string,
  options?: { notFound?: boolean; image?: string }
): MetaOptions {
  const settings = getSiteSettings();
  const image = options?.image ?? (gallery ? selectSocialImage({ kind: 'gallery', origin, gallery }) : undefined);
  const meta = baseMeta(origin, locale, path);

  if (options?.notFound) {
    const title = `Gallery not found | ${settings.site_title}`;
    const description = `The gallery you are looking for does not exist or is no longer publicly available on ${settings.site_title}.`;

    return {
      ...meta,
      title,
      description,
      robots: 'noindex,follow',
      og: {
        ...meta.og,
        title,
        description,
        image,
      },
      twitter: {
        ...meta.twitter,
        title,
        description,
        image,
      },
    };
  }

  if (!gallery) {
    const title = settings.site_title;
    const description = settings.seo_default_description;

    return {
      ...meta,
      title,
      description,
      robots: 'noindex,follow',
      og: {
        ...meta.og,
        title,
        description,
        image,
      },
      twitter: {
        ...meta.twitter,
        title,
        description,
        image,
      },
    };
  }

  const title = `${gallery.title} | ${settings.site_title}`;
  let description = gallery.description || `Exclusive public gallery in ${settings.site_title}.`;
  if (gallery?.hasProduct && gallery.product) {
    description = `${description} Available for ${gallery.displayPrice} ${gallery.product.currency}.`;
  }

  return {
    ...meta,
    title,
    description,
    robots: 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
      image,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
      image,
    },
  };
}

function buildExistingProfileDescription(profile: PublicProfile, siteTitle: string): string {
  const bio = profile.bio?.trim();
  if (bio) {
    return bio;
  }

  const hasOwnGalleries = profile.stats.own_galleries_count > 0;
  const hasCollaborations = profile.stats.collaborations_count > 0;

  if (hasOwnGalleries && hasCollaborations) {
    return `Perfil público de ${profile.display_name} en ${siteTitle}, con galerías propias y colaboraciones.`;
  }

  if (hasOwnGalleries) {
    return `Perfil público de ${profile.display_name} en ${siteTitle}, con galerías publicadas.`;
  }

  if (hasCollaborations) {
    return `Perfil público de ${profile.display_name} en ${siteTitle}, con colaboraciones publicadas.`;
  }

  return `Perfil público de ${profile.display_name} en ${siteTitle}.`;
}

export function buildProfileMeta(
  origin: string,
  locale: string,
  profile: PublicProfile | null,
  slug: string,
  fallbackDescription: string,
  options?: { notFound?: boolean }
): MetaOptions {
  const settings = getSiteSettings();
  const image = profile ? selectSocialImage({ kind: 'profile', origin, profile }) : undefined;
  const meta = baseMeta(origin, locale, `/@${slug}`);

  if (options?.notFound) {
    const title = `Profile not found | ${settings.site_title}`;
    const description = fallbackDescription;

    return {
      ...meta,
      title,
      description,
      robots: 'noindex,follow',
      og: {
        ...meta.og,
        title,
        description,
        image,
        type: 'profile',
      },
      twitter: {
        ...meta.twitter,
        title,
        description,
        image,
      },
    };
  }

  if (!profile) {
    const title = settings.site_title;
    const description = settings.seo_default_description;

    return {
      ...meta,
      title,
      description,
      robots: 'noindex,follow',
      og: {
        ...meta.og,
        title,
        description,
        image,
        type: 'profile',
      },
      twitter: {
        ...meta.twitter,
        title,
        description,
        image,
      },
    };
  }

  const title = `${profile.display_name} | ${settings.site_title}`;
  const description = buildExistingProfileDescription(profile, settings.site_title);

  return {
    ...meta,
    title,
    description,
    robots: 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
      image,
      type: 'profile',
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
      image,
    },
  };
}

export function buildContactMeta(origin: string, locale: string): MetaOptions {
  const settings = getSiteSettings();
  const title = `Contacto | ${settings.site_title}`;
  const description = `Contacta al equipo de ${settings.site_title} para consultas, soporte o cotizaciones.`;
  const meta = baseMeta(origin, locale, '/contact');

  return {
    ...meta,
    title,
    description,
    og: {
      ...meta.og,
      title,
      description,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
    },
  };
}

export function buildTermsMeta(origin: string, locale: string): MetaOptions {
  const settings = getSiteSettings();
  const title = `Términos y condiciones | ${settings.site_title}`;
  const description = `Lee los términos y condiciones de uso de ${settings.site_title}.`;
  const meta = baseMeta(origin, locale, '/info/terms');

  return {
    ...meta,
    title,
    description,
    robots: 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
    },
  };
}

export function buildPrivacyMeta(origin: string, locale: string): MetaOptions {
  const settings = getSiteSettings();
  const title = `Politica de Privacidad | ${settings.site_title}`;
  const description = `Lee la politica de privacidad de ${settings.site_title}. Datos que recopilamos, como los usamos y tus derechos.`;
  const meta = baseMeta(origin, locale, '/info/privacy');

  return {
    ...meta,
    title,
    description,
    robots: 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
    },
  };
}

export function hasMeaningfulSearchQuery(query: LocationQuery): boolean {
  const keys = Object.keys(query);
  if (keys.length === 0) return false;

  return keys.some((key) => {
    const value = query[key];
    if (Array.isArray(value)) {
      return value.some((item) => typeof item === 'string' && item.trim().length > 0);
    }

    return typeof value === 'string' && value.trim().length > 0;
  });
}

export function buildSearchMeta(origin: string, locale: string, routeQuery: LocationQuery): MetaOptions {
  const hasQuery = hasMeaningfulSearchQuery(routeQuery);
  const settings = getSiteSettings();
  const title = hasQuery ? `Resultados de búsqueda | ${settings.site_title}` : `Buscar galerías | ${settings.site_title}`;
  const description = hasQuery
    ? `Resultados de búsqueda de galerías públicas en ${settings.site_title}.`
    : `Explora y busca galerías públicas en ${settings.site_title}.`;
  const meta = baseMeta(origin, locale, '/search');

  return {
    ...meta,
    title,
    description,
    robots: hasQuery ? 'noindex,follow' : 'index,follow',
    og: {
      ...meta.og,
      title,
      description,
    },
    twitter: {
      ...meta.twitter,
      title,
      description,
    },
  };
}
